import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { protocolsApi, type MainProtocol, ApiError } from "@/api/client";
import { usersApi } from "@/api/userClient";
import { MaintenanceTab } from "./settings/MaintenanceTab";
import { useAuthStore } from "@/stores/auth";
import { useTranslation } from "@/framework-originated/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// ---------------------------------------------------------------------------
// /settings — 系统设置(tabs)。本轮实现「协议类型」tab。
//
// v6→main 域重构:protocol_adapter_mapping 表已删。主协议(原厂)直接携带
// protocol_type(new-api ChannelType);id 自增主键,code 唯一自然键。适配器由
// 绑定站点决定,不再有 per-adapter 映射。
// ---------------------------------------------------------------------------

const NEWAPI_NAMES: Record<number, string> = {
  1: "OpenAI", 3: "Azure", 14: "Anthropic", 17: "Ali", 20: "OpenRouter", 24: "Gemini",
  33: "AWS", 34: "Cohere", 40: "SiliconFlow", 41: "VertexAI", 42: "Mistral", 43: "DeepSeek",
  45: "VolcEngine", 48: "xAI", 25: "Moonshot", 59: "Sub2API", 60: "New API",
};

export function SettingsPage() {
  const { t } = useTranslation("users");
  const role = useAuthStore((s) => s.user?.role);
  const isSys = role === "sys";
  const [tab, setTab] = useState<"protocol" | "password" | "maintenance">("protocol");
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">系统设置</h1>
        <p className="mt-0.5 text-xs text-muted-foreground">平台级配置:协议类型字典、通用参数等。</p>
      </div>
      <div className="flex gap-0.5 border-b">
        <button
          className={`-mb-px border-b-2 px-4 py-2 text-sm ${tab === "protocol" ? "border-primary font-semibold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          onClick={() => setTab("protocol")}
        >
          协议类型
        </button>
        <button
          className={`-mb-px border-b-2 px-4 py-2 text-sm ${tab === "password" ? "border-primary font-semibold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          onClick={() => setTab("password")}
        >
          {t("settings.accountSecurity")}
        </button>
        {isSys && (
          <button
            className={`-mb-px border-b-2 px-4 py-2 text-sm ${tab === "maintenance" ? "border-primary font-semibold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            onClick={() => setTab("maintenance")}
          >
            运维清理
          </button>
        )}
        <span className="-mb-px cursor-not-allowed border-b-2 border-transparent px-4 py-2 text-sm text-muted-foreground/50">
          通用参数<span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px]">待建</span>
        </span>
        <span className="-mb-px cursor-not-allowed border-b-2 border-transparent px-4 py-2 text-sm text-muted-foreground/50">
          Webhook<span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px]">待建</span>
        </span>
      </div>
      {tab === "protocol" && <ProtocolTab />}
      {tab === "password" && <AccountSecurityTab />}
      {tab === "maintenance" && isSys && <MaintenanceTab />}
    </div>
  );
}

function ProtocolTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["protocols"], queryFn: protocolsApi.list, staleTime: 30_000 });
  const items = q.data?.items ?? [];

  const [search, setSearch] = useState("");
  const [enabledFilter, setEnabledFilter] = useState<"all" | "true" | "false">("all");
  const [drawer, setDrawer] = useState<MainProtocol | "new" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const delMut = useMutation({
    mutationFn: (code: string) => protocolsApi.remove(code),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["protocols"] }); setMsg("已删除主协议"); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : "删除失败"),
  });

  const filtered = items.filter((m) => {
    const s = search.trim().toLowerCase();
    const matchSearch = !s || [m.code, m.name, m.base_url].join(" ").toLowerCase().includes(s);
    const matchEnabled = enabledFilter === "all" || m.enabled === (enabledFilter === "true");
    return matchSearch && matchEnabled;
  });

  return (
    <>
      <div className="rounded-md bg-blue-50 px-3.5 py-2.5 text-xs leading-relaxed text-blue-900">
        <strong>主协议类型(原厂)</strong>是稳定锚点(<code className="rounded bg-black/5 px-1">code</code> + 名称 + <code className="rounded bg-black/5 px-1">protocol_type</code> + 默认 Base URL)。<code className="rounded bg-black/5 px-1">protocol_type</code> 为 new-api <code className="rounded bg-black/5 px-1">constant/channel.go</code> 的 ChannelType,决定下游渠道类型;适配器由绑定站点决定。
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input className="max-w-xs" placeholder="搜索 code / 名称 / Base URL…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select
          className="h-9 rounded-md border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
          value={enabledFilter}
          onChange={(e) => setEnabledFilter(e.target.value as "all" | "true" | "false")}
        >
          <option value="all">启用否:全部</option>
          <option value="true">启用</option>
          <option value="false">禁用</option>
        </select>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span><strong className="text-foreground">{filtered.length}</strong> / {items.length} 个主协议</span>
          {q.isError && <Badge variant="warning">API 不可达</Badge>}
          <Button size="sm" onClick={() => setDrawer("new")}><Plus className="mr-1 h-3.5 w-3.5" />新增主协议</Button>
        </div>
      </div>

      {msg && <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">{msg}</div>}

      <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left text-xs font-medium text-muted-foreground">
              <tr>
                <th className="px-3 py-2.5 w-[130px]">Code</th>
                <th className="px-3 py-2.5 w-[180px]">名称</th>
                <th className="px-3 py-2.5 w-[150px]">协议类型</th>
                <th className="px-3 py-2.5">默认 Base URL</th>
                <th className="px-3 py-2.5 w-[90px]">启用否</th>
                <th className="px-3 py-2.5 w-[90px] text-center">操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.code} className="cursor-pointer border-b hover:bg-accent/40" onClick={() => setDrawer(m)}>
                  <td className="px-3 py-2.5"><span className="rounded bg-violet-100 px-2 py-0.5 font-mono text-xs font-semibold text-violet-700">{m.code}</span></td>
                  <td className="px-3 py-2.5">{m.name}</td>
                  <td className="px-3 py-2.5">
                    <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs">{m.protocol_type}</span>
                    {NEWAPI_NAMES[m.protocol_type] && <span className="ml-1 text-[11px] text-muted-foreground">({NEWAPI_NAMES[m.protocol_type]})</span>}
                  </td>
                  <td className={`px-3 py-2.5 font-mono text-xs ${m.base_url ? "" : "italic text-muted-foreground"}`}>{m.base_url || "— 按实例/区域填 —"}</td>
                  <td className="px-3 py-2.5">
                    {m.enabled ? <Badge variant="success">启用</Badge> : <Badge variant="muted">禁用</Badge>}
                  </td>
                  <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                    <button className="rounded p-1 text-muted-foreground hover:bg-accent" title="编辑" onClick={() => setDrawer(m)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      title="删除主协议"
                      onClick={() => { if (confirm(`确认删除主协议「${m.name}」(code=${m.code})?`)) delMut.mutate(m.code); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-sm text-muted-foreground">{q.isLoading ? "加载中…" : "无主协议"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {drawer && (
        <ProtocolDrawer
          proto={drawer === "new" ? null : drawer}
          onClose={() => setDrawer(null)}
          onChanged={() => qc.invalidateQueries({ queryKey: ["protocols"] })}
        />
      )}
    </>
  );
}

function ProtocolDrawer({ proto, onClose, onChanged }: { proto: MainProtocol | null; onClose: () => void; onChanged: () => void }) {
  const isNew = proto == null;
  const [code, setCode] = useState(proto?.code ?? "");
  const [name, setName] = useState(proto?.name ?? "");
  const [protocolType, setProtocolType] = useState(String(proto?.protocol_type ?? ""));
  const [base, setBase] = useState(proto?.base_url ?? "");
  const [enabled, setEnabled] = useState(proto?.enabled ?? true);
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const c = code.trim();
      if (!/^[a-z0-9-]+$/.test(c)) throw new Error("code 只能是小写字母/数字/连字符");
      if (name.trim() === "") throw new Error("名称不能为空");
      const t = Number(protocolType);
      if (!Number.isInteger(t) || t <= 0) throw new Error("协议类型(protocol_type)必须为正整数");
      const body = { name: name.trim(), protocol_type: t, base_url: base.trim(), enabled };
      return isNew
        ? protocolsApi.create({ code: c, ...body })
        : protocolsApi.update(proto!.code, body);
    },
    onSuccess: () => { onChanged(); onClose(); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : "保存失败"),
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose}>
      <div className="flex h-full w-[520px] max-w-[94vw] flex-col bg-card shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="border-b px-5 py-4">
          <div className="flex items-center gap-2.5">
            <span className="rounded bg-violet-100 px-2 py-0.5 font-mono text-xs font-semibold text-violet-700">{code || "新主协议"}</span>
            <h3 className="text-base font-semibold">{isNew ? "新增主协议" : proto!.name}</h3>
            <button className="ml-auto text-muted-foreground hover:text-foreground" onClick={onClose}><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {err && <div className="mb-3 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Code <span className="text-destructive">*</span></Label>
              <Input className="font-mono" value={code} disabled={!isNew} onChange={(e) => setCode(e.target.value)} placeholder="openai" />
              <p className="text-[11px] text-muted-foreground">稳定标识,建后不可改。</p>
            </div>
            <div className="space-y-1">
              <Label>名称 <span className="text-destructive">*</span></Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="OpenAI 官方" />
            </div>
          </div>
          <div className="mt-3 space-y-1">
            <Label>协议类型 protocol_type <span className="text-destructive">*</span></Label>
            <Input type="number" min={1} className="font-mono" value={protocolType} onChange={(e) => setProtocolType(e.target.value)} placeholder="1" />
            <p className="text-[11px] text-muted-foreground">
              new-api ChannelType(OpenAI=1 / Anthropic=14 / AWS=33 / Gemini=24 / Vertex=41 …)。
              {NEWAPI_NAMES[Number(protocolType)] && <> 当前:<b>{NEWAPI_NAMES[Number(protocolType)]}</b></>}
            </p>
          </div>
          <div className="mt-3 space-y-1">
            <Label>默认 Base URL</Label>
            <Input className="font-mono" value={base} onChange={(e) => setBase(e.target.value)} placeholder="https://api.openai.com(留空 = 按实例/区域填)" />
            <p className="text-[11px] text-muted-foreground">AWS / Vertex / Azure 通常留空。</p>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              id="protocol-enabled"
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            <Label htmlFor="protocol-enabled" className="cursor-pointer text-sm">启用</Label>
            <span className="text-[11px] text-muted-foreground">禁用后新建/分发不选该协议。</span>
          </div>
        </div>

        <div className="border-t px-5 py-3 text-right">
          <Button variant="outline" size="sm" onClick={onClose} className="mr-2">关闭</Button>
          <Button size="sm" onClick={() => { setErr(null); save.mutate(); }} disabled={save.isPending}>
            {save.isPending ? "保存中…" : isNew ? "创建" : "保存"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AccountSecurityTab() {
  const { t } = useTranslation("users");
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const changeMut = useMutation({
    mutationFn: () =>
      usersApi.changeOwnPassword({ current_password: current, new_password: next }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      setErr(null);
      setMsg(t("settings.updateSuccess"));
      logout();
      navigate({ to: "/login", replace: true });
    },
    onError: (e) => {
      setMsg(null);
      setErr(e instanceof ApiError ? e.message : t("settings.updateFailed"));
    },
  });

  const valid = next.length >= 8 && next !== current && confirm === next;

  return (
    <section className="max-w-md rounded-xl border bg-card shadow-sm">
      <header className="border-b px-5 py-3">
        <h2 className="text-sm font-semibold">{t("settings.changePassword")}</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{t("settings.accountSecurityDesc")}</p>
      </header>
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) changeMut.mutate();
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="op-cur">{t("settings.currentPassword")} *</Label>
          <Input
            id="op-cur"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="op-next">{t("settings.newPassword")} *</Label>
          <Input
            id="op-next"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            required
          />
          <p className="text-[11px] text-muted-foreground">{t("settings.passwordHint")}</p>
        </div>
        <div className="space-y-1">
          <Label htmlFor="op-confirm">{t("settings.confirmPassword")} *</Label>
          <Input
            id="op-confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
          />
        </div>
        {(msg || err) && (
          <div className={`rounded-md border px-3 py-2 text-xs ${err ? "border-destructive/40 bg-destructive/5 text-destructive" : "border-green-200 bg-green-50 text-green-800"}`}>
            {err ?? msg}
          </div>
        )}
        <Button type="submit" disabled={!valid || changeMut.isPending}>
          {changeMut.isPending ? "…" : t("settings.updatePassword")}
        </Button>
      </form>
    </section>
  );
}
